import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Flag, ClipboardList, TicketPercent, UserCog } from 'lucide-react';
import { AdminUsers } from './AdminUsers';
import { AdminCoupons } from './AdminCoupons';
import { AdminRequests } from './AdminRequests';
import { AdminReviews } from './AdminReviews';
import { AdminPayments } from './AdminPayments';
import { Wallet } from 'lucide-react';
import { ReportsList } from './ReportsList';
import { CountBadge } from './CountBadge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { 
  Users, 
  CreditCard, 
  LogOut, 
  Plus, 
  Search, 
  Building2,
  BookOpen,
  DollarSign,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  Edit,
  User,
  Lock,
  X,
  Eye,
  TrendingUp,
  FileText,
  KeyRound,
  ExternalLink,
  Ban,
  ShieldCheck
} from 'lucide-react';
import axios from 'axios';
import { AdminFinance, AdminPayoutBanner } from './AdminFinance';
import { AdminAIGlobalCard, EditorAISwitch } from './AdminAIControls';
import { EditorPasswordModal } from './EditorPasswordModal';
import { EditorPricingCell } from './EditorPricingCell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "/livrum-logo-light.webp";

const ExemptLimitCell = ({ editor, onSaved }) => {
  const [value, setValue] = useState(String(editor.exempt_limit ?? 10));
  const [saving, setSaving] = useState(false);
  const dirty = Number(value) !== (editor.exempt_limit ?? 10);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/editors/${editor.id}/exempt-limit`, { limite: Number(value) }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      onSaved(data.exempt_limit);
    } catch (err) {
      alert(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Erro ao salvar limite');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
      <span data-testid={`exempt-used-${editor.id}`} style={{ color: (editor.exempt_used ?? 0) > Number(value) ? '#e53e3e' : undefined }} title={(editor.exempt_used ?? 0) > Number(value) ? 'Há mais isentos vigentes que o novo limite; novos cadastros ficarão bloqueados' : ''}>{editor.exempt_used ?? 0} /</span>
      <Input type="number" min={0} value={value} onChange={(e) => setValue(e.target.value)} style={{ width: '70px', height: '32px' }} data-testid={`exempt-limit-input-${editor.id}`} />
      {dirty && (
        <Button size="sm" onClick={save} disabled={saving || value === '' || Number(value) < 0} data-testid={`exempt-limit-save-${editor.id}`}>
          {saving ? '...' : 'Salvar'}
        </Button>
      )}
    </div>
  );
};

const UniversalReaderCard = () => {
  const [reader, setReader] = useState(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  useEffect(() => {
    axios.get(`${API}/admin/universal-reader`, { headers }).then((r) => setReader(r.data)).catch(() => {});
  }, []);
  if (!reader) return null;
  const reset = async () => {
    if (!window.confirm("Resetar a senha do leitor universal para 'senha123'?")) return;
    try {
      await axios.post(`${API}/admin/universal-reader/reset-password`, {}, { headers });
      alert("Senha resetada para 'senha123'. O leitor universal deverá criar uma nova senha no próximo login.");
    } catch (err) {
      alert('Erro ao resetar senha');
    }
  };
  return (
    <div data-testid="universal-reader-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '1rem 1.25rem', marginBottom: '1.25rem', borderRadius: '10px', border: '1px solid rgba(20,184,166,0.35)', background: 'rgba(20,184,166,0.06)' }}>
      <div>
        <div style={{ fontWeight: 600, color: '#0f766e' }}>Leitor universal (oculto para as editoras)</div>
        <div style={{ fontSize: '0.85rem', color: '#475569' }} data-testid="universal-reader-info">{reader.email} · CPF {reader.cpf} · acesso a todas as bibliotecas</div>
      </div>
      <Button variant="ghost" size="sm" onClick={reset} title="Resetar senha para 'senha123'" style={{ color: '#d97706' }} data-testid="universal-reader-reset-btn">
        <KeyRound className="w-4 h-4" />
      </Button>
    </div>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [editors, setEditors] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('editors');
  const [openRequests, setOpenRequests] = useState(0);
  const [forcePwd, setForcePwd] = useState(() => !!JSON.parse(localStorage.getItem('userData') || '{}').must_change_password);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/suspension-requests`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => setOpenRequests(r.data.open)).catch(() => {});
  }, []);
  const [pendingReports, setPendingReports] = useState(0);
  const loadPendingReports = () => axios.get(`${API}/admin/reports/pending-count`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
    .then((r) => setPendingReports(r.data.count)).catch(() => {});
  useEffect(() => { loadPendingReports(); }, []);
  const [selectedEditor, setSelectedEditor] = useState(null);
  
  // Estados para formulário de cadastro
  const [showAddEditorForm, setShowAddEditorForm] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [addEditorLoading, setAddEditorLoading] = useState(false);
  const [addEditorError, setAddEditorError] = useState('');
  const [newEditorForm, setNewEditorForm] = useState({
    email: '',
    senha: '',
    adminNome: '',
    adminCpf: '',
    adminTelefone: '',
    editoraNome: '',
    editoraEmail: '',
    valorAssinatura: '',
    plataformaPublica: false
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('userData');
    
    if (!token || !user) {
      navigate('/login');
      return;
    }
    
    const parsedUser = JSON.parse(user);
    if (parsedUser.role !== 'superadmin') {
      navigate('/editor/dashboard');
      return;
    }
    
    setUserData(parsedUser);
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [editorsRes, subscribersRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/admin/editors`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/subscribers`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/payments`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setEditors(editorsRes.data || []);
      setSubscribers(subscribersRes.data || []);
      setPayments(paymentsRes.data || []);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('panelLastActivity');
    navigate('/login');
  };

  const handleNewEditorChange = (e) => {
    const { name, value } = e.target;
    setNewEditorForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddEditor = async (e) => {
    e.preventDefault();
    setAddEditorLoading(true);
    setAddEditorError('');
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/editors`, {
        email: newEditorForm.email,
        senha: newEditorForm.senha,
        adminNome: newEditorForm.adminNome,
        adminCpf: newEditorForm.adminCpf,
        adminTelefone: newEditorForm.adminTelefone,
        bibliotecaNome: newEditorForm.editoraNome,
        bibliotecaEmail: newEditorForm.editoraEmail,
        valorAssinatura: newEditorForm.plataformaPublica ? 0 : (parseFloat(newEditorForm.valorAssinatura) || 0),
        plataforma_publica: newEditorForm.plataformaPublica,
        libraryType: 'digital',
        isento: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowAddEditorForm(false);
      setNewEditorForm({
        email: '',
        senha: '',
        adminNome: '',
        adminCpf: '',
        adminTelefone: '',
        editoraNome: '',
        editoraEmail: '',
        valorAssinatura: ''
      });
      fetchData();
    } catch (err) {
      setAddEditorError(err.response?.data?.detail || 'Erro ao cadastrar editor');
    } finally {
      setAddEditorLoading(false);
    }
  };

  const handleResetEditorPassword = async (editorId, editorEmail) => {
    if (!confirm(`Resetar a senha de ${editorEmail} para 'senha123'?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/editors/${editorId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Senha de ${editorEmail} resetada para 'senha123'`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao resetar senha');
    }
  };

  const handleResetSubscriberPassword = async (subscriberId, subscriberEmail) => {
    if (!confirm(`Resetar a senha de ${subscriberEmail} para 'senha123'?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/subscribers/${subscriberId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Senha de ${subscriberEmail} resetada para 'senha123'. O leitor deverá criar uma nova senha no próximo login.`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao resetar senha');
    }
  };

  const filteredEditors = editors.filter(editor => 
    editor.adminNome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    editor.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    editor.bibliotecaNome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSubscribers = subscribers.filter(sub => {
    const matchesSearch = sub.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEditor = !selectedEditor || sub.editor_id === selectedEditor;
    return matchesSearch && matchesEditor;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Calcular estatísticas
  const totalEditors = editors.length;
  const totalSubscribers = subscribers.length;
  const totalRevenue = editors.reduce((sum, e) => sum + (e.valorAssinatura || 0) * (subscribers.filter(s => s.editor_id === e.id).length), 0);
  const platformFee = totalRevenue * 0.30; // 30% da plataforma

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#14b8a6' }} />
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  // Renderiza formulário de cadastro de editor
  const renderAddEditorForm = () => (
    <div className="add-user-page">
      <div className="page-header">
        <h1 className="page-title">Cadastrar Nova Editora</h1>
        <Button 
          variant="outline" 
          onClick={() => setShowAddEditorForm(false)}
          className="btn-secondary-custom"
        >
          <X className="w-4 h-4 mr-2" />
          Cancelar
        </Button>
      </div>

      <form onSubmit={handleAddEditor} className="user-form-container">
        {/* Credenciais */}
        <Card className="form-section">
          <CardHeader>
            <CardTitle className="section-title-form">
              <Lock className="w-5 h-5" />
              Credenciais de Acesso
            </CardTitle>
          </CardHeader>
          <CardContent className="form-grid">
            <div className="form-field">
              <Label htmlFor="email">E-mail de Login *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={newEditorForm.email}
                onChange={handleNewEditorChange}
                required
              />
            </div>
            <div className="form-field">
              <Label htmlFor="senha">Senha *</Label>
              <Input
                id="senha"
                name="senha"
                type="password"
                value={newEditorForm.senha}
                onChange={handleNewEditorChange}
                required
              />
            </div>
          </CardContent>
        </Card>

        {/* Informações do Responsável */}
        <Card className="form-section">
          <CardHeader>
            <CardTitle className="section-title-form">
              <User className="w-5 h-5" />
              Responsável pela Editora
            </CardTitle>
          </CardHeader>
          <CardContent className="form-grid">
            <div className="form-field">
              <Label htmlFor="adminNome">Nome Completo *</Label>
              <Input
                id="adminNome"
                name="adminNome"
                value={newEditorForm.adminNome}
                onChange={handleNewEditorChange}
                required
              />
            </div>
            <div className="form-field">
              <Label htmlFor="adminCpf">CPF *</Label>
              <Input
                id="adminCpf"
                name="adminCpf"
                value={newEditorForm.adminCpf}
                onChange={handleNewEditorChange}
                required
              />
            </div>
            <div className="form-field">
              <Label htmlFor="adminTelefone">Telefone</Label>
              <Input
                id="adminTelefone"
                name="adminTelefone"
                value={newEditorForm.adminTelefone}
                onChange={handleNewEditorChange}
              />
            </div>
          </CardContent>
        </Card>

        {/* Informações da Editora */}
        <Card className="form-section">
          <CardHeader>
            <CardTitle className="section-title-form">
              <Building2 className="w-5 h-5" />
              Dados da Editora
            </CardTitle>
          </CardHeader>
          <CardContent className="form-grid">
            <div className="form-field">
              <Label htmlFor="editoraNome">Nome da Editora *</Label>
              <Input
                id="editoraNome"
                name="editoraNome"
                value={newEditorForm.editoraNome}
                onChange={handleNewEditorChange}
                required
              />
            </div>
            <div className="form-field">
              <Label htmlFor="editoraEmail">E-mail da Editora</Label>
              <Input
                id="editoraEmail"
                name="editoraEmail"
                type="email"
                value={newEditorForm.editoraEmail}
                onChange={handleNewEditorChange}
              />
            </div>
            <div className="form-field">
              <Label>Tipo de catálogo</Label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {[{ v: false, label: 'Pago (assinatura mensal)', id: 'paid' }, { v: true, label: 'Gratuito (público)', id: 'free' }].map((o) => (
                  <button key={o.id} type="button" data-testid={`new-editor-catalog-${o.id}`}
                    onClick={() => setNewEditorForm((f) => ({ ...f, plataformaPublica: o.v }))}
                    style={{
                      padding: '0.55rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem',
                      border: `2px solid ${newEditorForm.plataformaPublica === o.v ? '#14b8a6' : '#e2e8f0'}`,
                      background: newEditorForm.plataformaPublica === o.v ? 'rgba(20,184,166,0.1)' : 'white',
                      color: newEditorForm.plataformaPublica === o.v ? '#0f766e' : '#4a5568'
                    }}>{o.label}</button>
                ))}
              </div>
            </div>
            {!newEditorForm.plataformaPublica && (
              <div className="form-field">
                <Label htmlFor="valorAssinatura">Valor mensal da assinatura (R$ 10 a R$ 300) *</Label>
                <Input
                  id="valorAssinatura"
                  name="valorAssinatura"
                  type="number"
                  step="0.01"
                  min="10"
                  max="300"
                  required
                  value={newEditorForm.valorAssinatura}
                  onChange={handleNewEditorChange}
                  placeholder="Ex: 29.90"
                  data-testid="new-editor-price-input"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {addEditorError && (
          <div className="form-error">
            <AlertCircle className="w-4 h-4" />
            {addEditorError}
          </div>
        )}

        <div className="form-actions">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowAddEditorForm(false)}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="btn-primary-custom"
            disabled={addEditorLoading}
            style={{ background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)' }}
          >
            {addEditorLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Cadastrando...</>
            ) : (
              'Cadastrar Editora'
            )}
          </Button>
        </div>
      </form>
    </div>
  );

  // Renderiza página de editores
  const handleToggleCatalogSuspension = async (editor) => {
    const suspend = !editor.catalog_suspended;
    const msg = suspend
      ? `Suspender o catálogo de "${editor.bibliotecaNome}"?\n\nOs leitores verão uma mensagem de suspensão com o e-mail ${editor.email} e não poderão ler, assinar nem entrar.`
      : `Reativar o catálogo de "${editor.bibliotecaNome}"?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.put(`${API}/admin/editors/${editor.id}/catalog-suspension`, { suspended: suspend }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setEditors((list) => list.map((e) => (e.id === editor.id ? { ...e, catalog_suspended: suspend } : e)));
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao alterar suspensão');
    }
  };

  const renderEditorsPage = () => (
    <div className="users-page">
      <div className="page-header">
        <h1 className="page-title">Editoras Cadastradas</h1>
        <div className="page-actions">
          <div className="search-box">
            <Search className="search-icon" />
            <Input
              placeholder="Buscar editora..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <Button 
            className="btn-primary-custom"
            onClick={() => setShowAddEditorForm(true)}
            style={{ background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)' }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Editora
          </Button>
        </div>
      </div>

      {/* Cards de resumo */}
      <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
        <Card className="stat-card">
          <CardContent className="stat-content">
            <div className="stat-icon" style={{ background: 'rgba(20, 184, 166, 0.15)' }}>
              <Building2 className="w-6 h-6" style={{ color: '#14b8a6' }} />
            </div>
            <div className="stat-info">
              <span className="stat-label">Total de Editoras</span>
              <span className="stat-value">{totalEditors}</span>
            </div>
          </CardContent>
        </Card>
        
        <Card className="stat-card">
          <CardContent className="stat-content">
            <div className="stat-icon" style={{ background: 'rgba(30, 95, 138, 0.15)' }}>
              <Users className="w-6 h-6" style={{ color: '#1e5f8a' }} />
            </div>
            <div className="stat-info">
              <span className="stat-label">Total de Assinantes</span>
              <span className="stat-value">{totalSubscribers}</span>
            </div>
          </CardContent>
        </Card>
        
        <Card className="stat-card">
          <CardContent className="stat-content">
            <div className="stat-icon" style={{ background: 'rgba(15, 42, 74, 0.15)' }}>
              <FileText className="w-6 h-6" style={{ color: '#0f2a4a' }} />
            </div>
            <div className="stat-info">
              <span className="stat-label">Livros Publicados</span>
              <span className="stat-value">{editors.reduce((sum, e) => sum + (e.booksCount || 0), 0)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <AdminAIGlobalCard editors={editors} onGlobalChange={(enabled) => setEditors((list) => list.map((e) => ({ ...e, ai_enabled: enabled })))} />

      <Card className="data-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Editora</TableHead>
                <TableHead>Responsável</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>Assinatura</TableHead>
                <TableHead>Assinantes</TableHead>
                <TableHead>Livros</TableHead>
                <TableHead>Isentos (vigentes/limite)</TableHead>
                <TableHead>IA</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Cadastro</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEditors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8">
                    Nenhuma editora encontrada
                  </TableCell>
                </TableRow>
              ) : (
                filteredEditors.map((editor) => {
                  const editorSubscribers = subscribers.filter(s => s.editor_id === editor.id).length;
                  return (
                    <TableRow key={editor.id}>
                      <TableCell>
                        <div className="cell-main">{editor.bibliotecaNome}</div>
                        <div className="cell-sub">{editor.bibliotecaEmail}</div>
                      </TableCell>
                      <TableCell>{editor.adminNome}</TableCell>
                      <TableCell>{editor.email}</TableCell>
                      <TableCell>
                        <EditorPricingCell editor={editor} onSaved={(patch) => setEditors((list) => list.map((e) => e.id === editor.id ? { ...e, ...patch } : e))} />
                      </TableCell>
                      <TableCell>
                        <span style={{ 
                          background: 'rgba(20, 184, 166, 0.1)', 
                          color: '#14b8a6',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px',
                          fontWeight: '600',
                          fontSize: '0.85rem'
                        }}>
                          {editorSubscribers}
                        </span>
                      </TableCell>
                      <TableCell>{editor.booksCount || 0}</TableCell>
                      <TableCell>
                        <ExemptLimitCell editor={editor} onSaved={(limit) => setEditors((list) => list.map((e) => e.id === editor.id ? { ...e, exempt_limit: limit } : e))} />
                      </TableCell>
                      <TableCell>
                        <EditorAISwitch editor={editor} onSaved={(enabled) => setEditors((list) => list.map((e) => e.id === editor.id ? { ...e, ai_enabled: enabled } : e))} />
                      </TableCell>
                      <TableCell>
                        {editor.catalog_suspended
                          ? <span className="badge" data-testid={`admin-editor-status-${editor.id}`} style={{ background: 'rgba(239,68,68,0.12)', color: '#dc2626' }}>{editor.trial_status === 'expired' ? 'Teste encerrado' : 'Suspenso'}</span>
                          : editor.trial_status === 'active'
                            ? <span className="badge" data-testid={`admin-editor-status-${editor.id}`} style={{ background: '#fef3c7', color: '#92400e' }} title={`Teste termina em ${formatDate(editor.trial_ends_at)}`}>Teste até {formatDate(editor.trial_ends_at)}</span>
                            : <span className="badge badge-success" data-testid={`admin-editor-status-${editor.id}`}>Ativo</span>}
                      </TableCell>
                      <TableCell>{formatDate(editor.createdAt)}</TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedEditor(editor.id);
                              setActiveSection('subscribers');
                            }}
                            title="Ver assinantes"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResetEditorPassword(editor.id, editor.email)}
                            title="Resetar senha para 'senha123'"
                            style={{ color: '#d97706' }}
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`/${editor.slug || editor.id}`, '_blank', 'noopener')}
                            title="Abrir catálogo da editora"
                            style={{ color: '#14b8a6' }}
                            data-testid={`admin-editor-catalog-btn-${editor.id}`}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleCatalogSuspension(editor)}
                            title={editor.catalog_suspended ? 'Reativar catálogo da editora' : 'Suspender catálogo da editora'}
                            style={{ color: editor.catalog_suspended ? '#16a34a' : '#dc2626' }}
                            data-testid={`admin-editor-suspend-btn-${editor.id}`}
                          >
                            {editor.catalog_suspended ? <ShieldCheck className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  // Renderiza página de assinantes
  const renderSubscribersPage = () => (
    <div className="users-page">
      <UniversalReaderCard />
      <div className="page-header">
        <h1 className="page-title">
          Assinantes
          {selectedEditor && (
            <span style={{ fontSize: '1rem', fontWeight: 'normal', color: '#64748b', marginLeft: '0.5rem' }}>
              - {editors.find(e => e.id === selectedEditor)?.bibliotecaNome}
            </span>
          )}
        </h1>
        <div className="page-actions">
          <div className="search-box">
            <Search className="search-icon" />
            <Input
              placeholder="Buscar assinante..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          {selectedEditor && (
            <Button 
              variant="outline"
              onClick={() => setSelectedEditor(null)}
            >
              Ver Todos
            </Button>
          )}
        </div>
      </div>

      <Card className="data-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Assinante</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>CPF</TableHead>
                <TableHead>Editora</TableHead>
                <TableHead>Início</TableHead>
                <TableHead>Vencimento</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSubscribers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Nenhum assinante encontrado
                  </TableCell>
                </TableRow>
              ) : (
                filteredSubscribers.map((sub) => {
                  const editor = editors.find(e => e.id === sub.editor_id);
                  const isActive = new Date(sub.assinaturaFim) > new Date();
                  return (
                    <TableRow key={sub.id}>
                      <TableCell>
                        <div className="cell-main">{sub.nome}</div>
                      </TableCell>
                      <TableCell>{sub.email}</TableCell>
                      <TableCell>{sub.cpf}</TableCell>
                      <TableCell>{editor?.bibliotecaNome || '-'}</TableCell>
                      <TableCell>{formatDate(sub.assinaturaInicio)}</TableCell>
                      <TableCell>{formatDate(sub.assinaturaFim)}</TableCell>
                      <TableCell>
                        {isActive ? (
                          <span className="badge badge-success">Ativo</span>
                        ) : (
                          <span className="badge badge-error">Vencido</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResetSubscriberPassword(sub.id, sub.email)}
                          title="Resetar senha para 'senha123'"
                          style={{ color: '#d97706' }}
                        >
                          <KeyRound className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  // Renderiza página de relatório financeiro
  const renderFinancialPage = () => <AdminFinance />;

  return (
    <div className="admin-page">
      {/* Sidebar */}
      <aside className="admin-sidebar" style={{ background: '#0f2a4a' }}>
        <div className="sidebar-header">
          <img 
            src={LOGO_URL} 
            alt="Livrum" 
            style={{ height: '40px', width: 'auto' }} 
          />
        </div>
        
        <nav className="sidebar-nav">
          <span className="nav-section-title" style={{ color: 'rgba(255,255,255,0.5)' }}>Administração</span>
          <button 
            className={`nav-item ${activeSection === 'editors' ? 'active' : ''}`}
            onClick={() => { setActiveSection('editors'); setShowAddEditorForm(false); setSelectedEditor(null); }}
            style={activeSection === 'editors' ? { background: 'rgba(20, 184, 166, 0.2)', color: '#5eead4' } : { color: 'rgba(255,255,255,0.8)' }}
          >
            <Building2 className="w-5 h-5" />
            <span>Editoras</span>
          </button>
          <button 
            className={`nav-item ${activeSection === 'subscribers' ? 'active' : ''}`}
            onClick={() => { setActiveSection('subscribers'); setShowAddEditorForm(false); }}
            style={activeSection === 'subscribers' ? { background: 'rgba(20, 184, 166, 0.2)', color: '#5eead4' } : { color: 'rgba(255,255,255,0.8)' }}
          >
            <Users className="w-5 h-5" />
            <span>Assinantes</span>
          </button>
          <button 
            className={`nav-item ${activeSection === 'financial' ? 'active' : ''}`}
            onClick={() => { setActiveSection('financial'); setShowAddEditorForm(false); }}
            style={activeSection === 'financial' ? { background: 'rgba(20, 184, 166, 0.2)', color: '#5eead4' } : { color: 'rgba(255,255,255,0.8)' }}
          >
            <CreditCard className="w-5 h-5" />
            <span>Financeiro</span>
          </button>
          {[['reviews', Star, 'Avaliações'], ['reports', Flag, 'Denúncias'], ['payments', Wallet, 'Pagamentos'], ['requests', ClipboardList, 'Solicitações'], ['coupons', TicketPercent, 'Cupons'], ['admins', UserCog, 'Administradores']].map(([key, Icon, label]) => (
            <button key={key} className={`nav-item ${activeSection === key ? 'active' : ''}`} data-testid={`admin-menu-${key}`}
              onClick={() => { setActiveSection(key); setShowAddEditorForm(false); }}
              style={activeSection === key ? { background: 'rgba(20, 184, 166, 0.2)', color: '#5eead4' } : { color: 'rgba(255,255,255,0.8)' }}>
              <Icon className="w-5 h-5" />
              <span>{label}</span>
              {key === 'reports' && pendingReports > 0 && <CountBadge n={pendingReports} testId="admin-reports-badge" />}
              {key === 'requests' && openRequests > 0 && <CountBadge n={openRequests} testId="admin-requests-badge" />}
            </button>
          ))}
        </nav>
        
        <div className="sidebar-footer">
          <div className="user-info">
            <span className="user-name" style={{ color: 'white' }}>{userData?.adminNome || 'Admin'}</span>
            <span className="user-role" style={{ color: 'rgba(255,255,255,0.6)' }}>Administrador Geral</span>
          </div>
          <Button
            variant="ghost"
            onClick={() => setShowPasswordModal(true)}
            data-testid="admin-change-password-btn"
            style={{ color: 'rgba(255,255,255,0.85)', justifyContent: 'flex-start', marginBottom: '0.5rem', width: '100%' }}
          >
            <KeyRound className="w-4 h-4 mr-2" />
            Alterar senha
          </Button>
          {showPasswordModal && (
            <EditorPasswordModal headers={{ Authorization: `Bearer ${localStorage.getItem('token')}` }} onClose={() => setShowPasswordModal(false)} />
          )}
          {forcePwd && (
            <EditorPasswordModal forced headers={{ Authorization: `Bearer ${localStorage.getItem('token')}` }} onClose={() => {
              const u = JSON.parse(localStorage.getItem('userData') || '{}');
              localStorage.setItem('userData', JSON.stringify({ ...u, must_change_password: false }));
              setForcePwd(false);
            }} />
          )}
          <Button 
            variant="ghost" 
            className="logout-button"
            onClick={handleLogout}
            style={{ 
              background: 'white', 
              color: '#0f2a4a',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontWeight: 500
            }}
          >
            <LogOut className="w-5 h-5" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        {activeSection !== 'financial' && <AdminPayoutBanner />}
        {showAddEditorForm ? renderAddEditorForm() : (
          activeSection === 'editors' ? renderEditorsPage() :
          activeSection === 'subscribers' ? renderSubscribersPage() :
          activeSection === 'reviews' ? <AdminReviews /> :
          activeSection === 'reports' ? <ReportsList isAdmin onCountChange={loadPendingReports} /> :
          activeSection === 'payments' ? <AdminPayments /> :
          activeSection === 'requests' ? <AdminRequests onCountChange={setOpenRequests} /> :
          activeSection === 'coupons' ? <AdminCoupons /> :
          activeSection === 'admins' ? <AdminUsers /> :
          renderFinancialPage()
        )}
      </main>
    </div>
  );
};

export default AdminPage;
